import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale,
  PointElement, LineElement, Filler, Tooltip,
} from 'chart.js';
import { getAvailability, getResponseTime } from '../services/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

const DAYS = ['J-6','J-5','J-4','J-3','J-2','Hier','Auj.'];

const chartOpts = (color, unit, min, max) => ({
  responsive: true, maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: {
    callbacks: { label: (c) => `${c.parsed.y.toFixed(unit === '%' ? 2 : 0)} ${unit}` }
  }},
  scales: {
    x: { ticks: { font: { size: 9 }, color: '#94A3B8' }, grid: { display: false } },
    y: { min, max, ticks: { font: { size: 9 }, color: '#94A3B8',
         callback: (v) => `${v}${unit}` }, grid: { color: '#F1F5F9' } },
  },
});

function mkDataset(labels, values, color) {
  return {
    labels,
    datasets: [{
      data: values, borderColor: color, backgroundColor: color + '20',
      borderWidth: 2, pointRadius: 3, pointBackgroundColor: color,
      fill: true, tension: 0.4,
    }],
  };
}

export default function ApiCard({ service }) {
  const [open,       setOpen]       = useState(false);
  const [availData,  setAvailData]  = useState(null);
  const [rtData,     setRtData]     = useState(null);
  const [loading,    setLoading]    = useState(false);

  const statusColor = {
    OK:      '#16A34A', WARN: '#EA580C', DOWN: '#DC2626', UNKNOWN: '#94A3B8',
  }[service.last_status] || '#94A3B8';

  async function loadCharts() {
    if (availData) return; // déjà chargé
    setLoading(true);
    try {
      const [aRes, rRes] = await Promise.all([
        getAvailability(service.name, 7),
        getResponseTime(service.name, 7),
      ]);
      const labels = DAYS;
      const av = aRes.data.data.slice(-7).map((d) => d.value);
      const rt = rRes.data.data.slice(-7).map((d) => d.value);
      const avColor = av.some((v) => v < 99) ? '#EA580C' : '#16A34A';
      const rtColor = rt.some((v) => v > 500) ? '#DC2626' : '#2563EB';
      setAvailData(mkDataset(labels, av, avColor));
      setRtData(mkDataset(labels, rt, rtColor));
    } catch {}
    setLoading(false);
  }

  function toggle() {
    setOpen((o) => !o);
    if (!open) loadCharts();
  }

  const uptime = parseFloat(service.uptime_30d || 100).toFixed(2);

  return (
    <div style={{ background: '#fff', border: '1px solid #E2E8F0',
                  borderLeft: `4px solid ${statusColor}`,
                  borderRadius: 9, marginBottom: 8, overflow: 'hidden' }}>

      {/* En-tête cliquable */}
      <div onClick={toggle}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                 padding: '10px 14px', cursor: 'pointer' }}
        onMouseEnter={(e) => e.currentTarget.style.background = '#F8FAFC'}
        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                         background: '#DBEAFE', color: '#1D4ED8' }}>GET</span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#1B2A4A' }}>{service.name}</div>
            <div style={{ fontSize: 10, color: '#94A3B8', fontFamily: 'monospace' }}>
              {service.check_url}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: uptime >= 99 ? '#16A34A' : '#EA580C' }}>
              {uptime}%
            </div>
            <div style={{ fontSize: 9, color: '#94A3B8' }}>Disponibilité</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1B2A4A' }}>
              {service.last_response_ms ? `${service.last_response_ms} ms` : '—'}
            </div>
            <div style={{ fontSize: 9, color: '#94A3B8' }}>Temps moy.</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, fontWeight: 600,
                        padding: '2px 7px', borderRadius: 20,
                        background: { OK:'#DCFCE7', WARN:'#FFEDD5', DOWN:'#FEE2E2' }[service.last_status] || '#F1F5F9',
                        color: statusColor, textTransform: 'uppercase' }}>
            <span style={{ width: 5, height: 5, borderRadius:'50%', background: statusColor }} />
            {service.last_status || 'UNKNOWN'}
          </div>
          <span style={{ color: '#CBD5E1', fontSize: 16, transform: open ? 'rotate(180deg)' : 'none',
                         transition: 'transform .2s' }}>▾</span>
        </div>
      </div>

      {/* Corps avec graphiques */}
      {open && (
        <div style={{ borderTop: '1px solid #F1F5F9', padding: 14, background: '#F8FAFC' }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#94A3B8', fontSize: 12, padding: 20 }}>
              Chargement des métriques…
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#64748B',
                              textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>
                  📈 Disponibilité 7 jours (%)
                </div>
                <div style={{ height: 88 }}>
                  {availData && (
                    <Line data={availData}
                      options={chartOpts('#16A34A', '%',
                        Math.min(...availData.datasets[0].data) - 1, 100.2)} />
                  )}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#64748B',
                              textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>
                  ⚡ Temps de réponse 7 jours (ms)
                </div>
                <div style={{ height: 88 }}>
                  {rtData && (
                    <Line data={rtData}
                      options={chartOpts('#2563EB', 'ms', 0,
                        Math.max(...rtData.datasets[0].data) * 1.2)} />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
